/**
 * Cola de solicitudes de eliminación de cuenta (Sprint 18 P10, Ley 21.719).
 *
 * La Comisión revisa las solicitudes pendientes y las ejecuta (anonimiza la
 * persona) o las rechaza con motivo. Mínima funcional — el pulido va a P12.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  ejecutarEliminacion,
  listarEliminaciones,
  rechazarEliminacion,
  type SolicitudEliminacionConPersonaRead,
} from '../../lib/api/ley21719';
import { formatFecha } from '../../lib/format';

export function Eliminaciones() {
  const [solicitudes, setSolicitudes] = useState<
    SolicitudEliminacionConPersonaRead[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [rechazando, setRechazando] =
    useState<SolicitudEliminacionConPersonaRead | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);

  function abrirModalRechazo(s: SolicitudEliminacionConPersonaRead) {
    setRechazando(s);
    setMotivoRechazo('');
    setErrorModal(null);
  }

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      setSolicitudes(await listarEliminaciones('pendiente'));
    } catch {
      setError('No pudimos cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void cargar();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleEjecutar(s: SolicitudEliminacionConPersonaRead) {
    if (procesando) return;
    setProcesando(true);
    try {
      await ejecutarEliminacion(s.id);
      setToast(`Datos de ${s.persona_nombre} anonimizados.`);
      await cargar();
    } catch (err) {
      setToast(
        err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : 'No se pudo ejecutar.',
      );
    } finally {
      setProcesando(false);
    }
  }

  async function handleRechazar(e: FormEvent) {
    e.preventDefault();
    if (!rechazando || procesando) return;
    if (!motivoRechazo.trim()) {
      setErrorModal('El motivo es obligatorio.');
      return;
    }
    setProcesando(true);
    setErrorModal(null);
    try {
      await rechazarEliminacion(rechazando.id, motivoRechazo.trim());
      setRechazando(null);
      setMotivoRechazo('');
      setToast('Solicitud rechazada.');
      await cargar();
    } catch {
      setErrorModal('No se pudo rechazar.');
    } finally {
      setProcesando(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-calbucura-black">
          Solicitudes de eliminación de cuenta
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Ley 21.719. Ejecutar anonimiza los datos personales del vecino
          (conserva lo contable). Solo se permite si no tiene tenencias activas.
        </p>
      </div>

      {loading && (
        <div className="bg-white rounded-lg shadow p-5 text-sm text-gray-500">
          Cargando…
        </div>
      )}
      {error && !loading && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded text-sm text-gray-800">
          {error}
        </div>
      )}

      {!loading && !error && solicitudes.length === 0 && (
        <div className="bg-white rounded-lg shadow p-5 text-sm text-gray-500">
          No hay solicitudes pendientes.
        </div>
      )}

      {!loading &&
        solicitudes.map((s) => (
          <article key={s.id} className="bg-white rounded-lg shadow p-4">
            <header className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
              <div>
                <p className="font-medium text-calbucura-black">
                  {s.persona_nombre}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Solicitada el {formatFecha(s.creado_at.slice(0, 10))}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => abrirModalRechazo(s)}
                  disabled={procesando}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={() => handleEjecutar(s)}
                  disabled={procesando}
                  className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  Ejecutar (anonimizar)
                </button>
              </div>
            </header>
            {s.motivo && (
              <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                <span className="font-medium">Motivo del vecino:</span>{' '}
                {s.motivo}
              </p>
            )}
          </article>
        ))}

      {rechazando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-calbucura-black mb-1">
              Rechazar solicitud
            </h3>
            <p className="text-sm text-gray-600 mb-4">{rechazando.persona_nombre}</p>
            <form onSubmit={handleRechazar} className="space-y-4">
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                rows={3}
                required
                disabled={procesando}
                className="w-full rounded-md border-gray-300 text-sm"
                placeholder="Ej: tiene deuda pendiente / tenencia activa"
              />
              {errorModal && (
                <p className="text-sm text-red-700 bg-red-50 border-l-4 border-red-500 p-2 rounded">
                  {errorModal}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRechazando(null)}
                  disabled={procesando}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={procesando}
                  className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  Rechazar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 bg-calbucura-black text-white px-4 py-2 rounded shadow-lg text-sm z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
